// webserver/frontend/src/app/providers.tsx
"use client"

import { SessionProvider, useSession }             from "next-auth/react"
import { QueryClient, QueryClientProvider }        from "@tanstack/react-query"
import { useQueryClient as useQueryClient_inner }  from "@tanstack/react-query"
import { useState, useEffect, useRef,
         createContext, useContext }               from "react"
import { save_token, load_token, clear_token,
         save_role,  load_role,  clear_role  }     from "@/lib/token-store"


// ── Token context ──────────────────────────────────────────

const TokenContext = createContext<string>("")
const RoleContext  = createContext<string>("")

export function useToken(): string { return useContext(TokenContext) }
export function useRole():  string { return useContext(RoleContext)  }

// ── Mounted guard — shared hydration helper ────────────────
// useToken() returns "" on the server (no localStorage).
// Any component that gates on !!token must suppress loading
// states until after first client mount to avoid hydration mismatch.

export function useMounted(): boolean {
  const [mounted, set_mounted] = useState(false)
  useEffect(() => { set_mounted(true) }, [])
  return mounted
}


function TokenProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession()

  const [token, set_token] = useState<string>(() => {
    if (typeof window === "undefined") return ""
    return load_token()
  })

  const [role, set_role] = useState<string>(() => {
    if (typeof window === "undefined") return ""
    return load_role()
  })

  useEffect(() => {
    if (status === "loading") return

    const live_token = session?.accessToken    ?? ""
    const live_role  = (session?.user?.role as string) ?? ""

    if (live_token) {
      save_token(live_token)
      set_token(live_token)
    }

    if (live_role) {
      save_role(live_role)
      set_role(live_role)
    }
    // Never auto-clear — expired token gets 401 from API.
  }, [session?.accessToken, session?.user?.role, status])

  return (
    <TokenContext.Provider value={token}>
      <RoleContext.Provider value={role}>
        {children}
      </RoleContext.Provider>
    </TokenContext.Provider>
  )
}


// ── Session watcher — clears query cache on user switch ────

function SessionWatcher() {
  const { data: session } = useSession()
  const query_client      = useQueryClient_inner()
  const prev_user         = useRef<string | null>(null)

  useEffect(() => {
    const current_user = session?.user?.email ?? null
    if (prev_user.current !== null && prev_user.current !== current_user) {
      query_client.clear()
      try {
        clear_token()
        clear_role()
      } catch { /* ignore */ }
    }
    prev_user.current = current_user
  }, [session?.user?.email, query_client])

  return null
}


// ── Root providers ─────────────────────────────────────────

export default function Providers({ children }: { children: React.ReactNode }) {
  const [query_client] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime:            1000 * 60 * 10,
        gcTime:               1000 * 60 * 60,
        retry:                1,
        refetchOnWindowFocus: false,
        refetchOnReconnect:   true,
      },
    },
  }))

  return (
    <SessionProvider refetchOnWindowFocus={false} refetchWhenOffline={false}>
      <QueryClientProvider client={query_client}>
        <TokenProvider>
          <SessionWatcher />
          {children}
        </TokenProvider>
      </QueryClientProvider>
    </SessionProvider>
  )
}
